import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useToast } from '../components/Toast';

export interface AttachmentRecipient {
  id: string;
  userId: string;
  downloadedAt: string | null;
  user?: { id: string; name: string };
}

export interface Attachment {
  id: string;
  resourceType: 'file' | 'link';
  fileName: string;
  mimeType?: string | null;
  fileSize?: number | null;
  externalUrl?: string | null;
  createdAt: string;
  uploadedBy?: { id: string; name: string };
  recipients?: AttachmentRecipient[];
}

export interface Participant { id: string; name: string; email: string }

export function useAttachments(ticketId: string | undefined) {
  const { toast } = useToast();
  const [attachments, setAttachments]   = useState<Attachment[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading]   = useState(true);
  const [uploading, setUploading] = useState(false);

  const refetch = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const res = await api.get(`/tickets/${ticketId}/attachments`);
      setAttachments(res.data.attachments ?? []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  const loadParticipants = useCallback(async () => {
    if (!ticketId) return;
    try {
      const res = await api.get(`/tickets/${ticketId}/attachments/participants`);
      setParticipants(res.data.participants ?? []);
    } catch {
      /* silent */
    }
  }, [ticketId]);

  useEffect(() => { refetch(); }, [refetch]);

  // One-time file, sent to one or more of the ticket's current participants —
  // each gets their own independent download (see AttachmentRecipient).
  const upload = async (file: File, recipientIds: string[]) => {
    if (!ticketId) return;
    if (!recipientIds.length) { toast('Select at least one person this file is for', 'error'); return; }
    setUploading(true);
    const form = new FormData();
    form.append('file', file);
    form.append('recipientIds', JSON.stringify(recipientIds));
    try {
      await api.post(`/tickets/${ticketId}/attachments`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast('File sent', 'success');
      await refetch();
    } catch (e: any) {
      toast(e?.response?.data?.error || 'Upload failed', 'error');
    } finally {
      setUploading(false);
    }
  };

  // No separate name field — the server derives a display name from the URL itself.
  const addLink = async (externalUrl: string) => {
    if (!ticketId) return;
    setUploading(true);
    try {
      await api.post(`/tickets/${ticketId}/attachments/link`, { externalUrl });
      toast('Link added', 'success');
      await refetch();
    } catch (e: any) {
      toast(e?.response?.data?.error || e?.response?.data?.message || 'Could not add link', 'error');
    } finally {
      setUploading(false);
    }
  };

  const download = async (att: Attachment) => {
    if (!ticketId) return;
    // A link attachment has nothing to fetch — the URL is already in the list
    // response (same ticket-visibility check as everything else), so just open it.
    if (att.resourceType === 'link') {
      if (att.externalUrl) window.open(att.externalUrl, '_blank', 'noopener');
      return;
    }
    try {
      const res = await api.get(`/tickets/${ticketId}/attachments/${att.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      // Give the new tab time to actually load the file before freeing the URL.
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      // The server consumes your copy the moment this download finishes (and
      // removes the file outright once every recipient has done the same) —
      // refetch so the list reflects that instead of showing stale state.
      toast('Downloaded — your copy of this file has been removed', 'success');
      await refetch();
    } catch (e: any) {
      toast(e?.response?.data?.error || 'Could not download attachment', 'error');
    }
  };

  const remove = async (att: Attachment) => {
    if (!ticketId) return;
    try {
      await api.delete(`/tickets/${ticketId}/attachments/${att.id}`);
      toast('Attachment removed', 'success');
      await refetch();
    } catch (e: any) {
      toast(e?.response?.data?.error || 'Delete failed', 'error');
    }
  };

  return { attachments, participants, loading, uploading, refetch, loadParticipants, upload, addLink, download, remove };
}
